import AddIcon from '@mui/icons-material/Add';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { compose } from 'redux';
import { getAuthPersonSelector } from '@redux/oauth/oauthSelectors';
import { getActiveOrganizationIdSelector } from '@redux/organization/organizationSelectors';
import { getActivePersonIdNamePairByPersonIdSelector } from '@redux/person/personSelector';
import { getAllActivePersonThunk, getPersonByIdThunk, setSelectedPatientIdThunk } from '@redux/person/personThunks';
import { PersonIdNameTimezone } from '@redux/person/personTypes';
import { getOrganizationTasksListSelector } from '@redux/tasks/tasksSelector';
import { getTasksListThunk, postNewTaskThunk } from '@redux/tasks/tasksThunks';
import { TaskCreationPayloadType } from '@redux/tasks/tasksTypes';
import { ReduxState } from '@redux/types';
import { useAlert } from 'components/AlertInfoGlobal/useAlert';
import { Button } from 'components/Button/Button';
import NavTopBarContentWrapper from 'components/NavTopBarContentWrapper/NavTopBarContentWrapper';
import CancelTaskModal from 'containers/Tasks/components/CancelTaskModal/CancelTaskModal';
import { EmptyTasksListArea } from 'containers/Tasks/components/EmptyTasksListArea/EmptyTasksListArea';
import TaskCreationDrawer from 'containers/Tasks/components/TaskCreationDrawer/TaskCreationDrawer';
import TaskInfoDrawer from 'containers/Tasks/components/TaskInfoDrawer/TaskInfoDrawer';
import { TasksTable } from 'containers/Tasks/components/TasksTable/TasksTable';
import { customDateTimePickerStyles } from '../../customStyles';
import style from './Tasks.scss';

const FETCH_TASKS_STATE_INTERVAL = 60000;
type Props = ConnectedProps<typeof connectRedux>;

const Tasks: React.FC<Props> = ({ activeOrganizationId, activePersonByIdSelector, getAllActivePerson, postNewTask, fetchTasksList, tasksList = [] }) => {
  const organizationId = activeOrganizationId;
  const [initDone, setInitDone] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<string>('');
  const [cancelTaskOptions, setCancelTaskOptions] = useState<{ id: string; isSingleCancel: boolean } | null>(null);
  const [addingTask, setAddingTask] = useState<boolean>(false);
  const [activePersonList, setActivePersonList] = useState<PersonIdNameTimezone[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());

  const { showAlertInfo, showAlertWarning } = useAlert();

  const debouncedFetch = useMemo(
    () =>
      debounce(async (...args: Parameters<typeof fetchTasksList>) => {
        await fetchTasksList(...args);
        !initDone && setInitDone(true);
      }, 500),
    [fetchTasksList, initDone]
  );

  useEffect(() => {
    debouncedFetch(organizationId, selectedDate);

    const intervalId = setInterval(() => debouncedFetch(organizationId, selectedDate), FETCH_TASKS_STATE_INTERVAL);

    return () => {
      debouncedFetch.cancel();
      clearInterval(intervalId);
    };
  }, [organizationId, selectedDate, debouncedFetch]);

  useEffect(() => {
    getAllActivePerson(organizationId);
  }, [organizationId]);

  useEffect(() => {
    const getActivePersons = (): PersonIdNameTimezone[] => {
      const activePersons: PersonIdNameTimezone[] = [];
      if (!activePersonByIdSelector) {
        return activePersons;
      }
      for (const personId of Object.keys(activePersonByIdSelector)) {
        const personIdNamePair = activePersonByIdSelector[personId];
        if (!personIdNamePair) {
          continue;
        }

        const activePerson: PersonIdNameTimezone = {
          ...personIdNamePair,
          fullName: personIdNamePair.fullName ?? ''
        };
        activePersons.push(activePerson);
      }

      return activePersons;
    };

    setActivePersonList(getActivePersons());
  }, [activePersonByIdSelector]);

  const addNewTask = () => {
    setAddingTask(true);
  };

  const handleSubmitAddNewTask = useCallback(
    async (task: TaskCreationPayloadType) => {
      try {
        await postNewTask(task);
        setAddingTask(false);
        showAlertInfo({
          message: 'Task created successfully!'
        });
      } catch (err) {
        showAlertWarning({
          message: 'Something went wrong creating your task. Please try again.'
        });
      }
    },
    [selectedDate]
  );

  const onSelectTaskSwitch = useCallback((id: string = '') => {
    setSelectedTaskId(id);
  }, []);

  const showConfirmationModal = useCallback((taskId: string = '', isSingleCancel: boolean) => {
    setCancelTaskOptions({ id: taskId, isSingleCancel });
  }, []);

  const hideConfirmationModal = useCallback(() => {
    setCancelTaskOptions(null);
  }, []);

  const handleCancelSuccess = useCallback(() => {
    // Close the task info drawer by clearing selected task
    setSelectedTaskId('');
    // Refresh tasks data
    fetchTasksList(organizationId, selectedDate);
  }, [organizationId, selectedDate, fetchTasksList]);

  return (
    <NavTopBarContentWrapper
      headerText="Tasks"
      contentClassName={style.contentWrapper}
      headerContent={
        initDone && tasksList.length ? (
          <Button variant="contained" onClick={addNewTask} startIcon={<AddIcon />}>
            Add Task
          </Button>
        ) : null
      }
    >
      <div className={style.filtersContainer}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            value={selectedDate}
            onChange={(newDate) => {
              if (newDate) {
                setSelectedDate(newDate);
              }
            }}
            format="MMM dd, yyyy"
            className={style.datePicker}
            sx={customDateTimePickerStyles}
          />
        </LocalizationProvider>
      </div>
      {initDone && !!tasksList.length && <TasksTable data={tasksList} onSelectTaskSwitch={onSelectTaskSwitch} onCancelTask={showConfirmationModal} />}
      {initDone && !tasksList.length && <EmptyTasksListArea onAddPress={addNewTask} />}
      {selectedTaskId && <TaskInfoDrawer taskId={selectedTaskId} onClose={() => onSelectTaskSwitch()} onCancelTask={showConfirmationModal} />}
      {addingTask && (
        <TaskCreationDrawer
          activePersons={activePersonList}
          visible={true}
          onClose={() => {
            setAddingTask(false);
          }}
          completeSubmitNewTask={handleSubmitAddNewTask}
          onSuccessSubmit={() => {
            fetchTasksList(organizationId, selectedDate);
          }}
        ></TaskCreationDrawer>
      )}
      <CancelTaskModal
        taskId={cancelTaskOptions?.id}
        visible={!!cancelTaskOptions?.id}
        isSingleCancel={cancelTaskOptions?.isSingleCancel}
        onConfirmPress={onSelectTaskSwitch}
        onClosePress={hideConfirmationModal}
        onSuccess={handleCancelSuccess}
      />
    </NavTopBarContentWrapper>
  );
};

const connectRedux = connect(
  (state: ReduxState) => ({
    activeOrganizationId: getActiveOrganizationIdSelector(state),
    tasksList: getOrganizationTasksListSelector(state),
    activePersonByIdSelector: getActivePersonIdNamePairByPersonIdSelector(state)
  }),
  {
    fetchTasksList: getTasksListThunk,
    setSelectedPatientIdThunkCallback: setSelectedPatientIdThunk,
    fetchPersonById: getPersonByIdThunk,
    getAllActivePerson: getAllActivePersonThunk,
    postNewTask: postNewTaskThunk
  }
);

export default compose(connectRedux)(Tasks);
